import { useEffect } from 'preact/hooks';
import { Footer } from '@/components/Footer';
import { Header } from '@/components/Header';
import { Browse } from '@/pages/Browse';
import { NotFound } from '@/pages/NotFound';
import { RecipeList } from '@/pages/RecipeList';
import { RecipeView } from '@/pages/RecipeView';
import { recipes } from '@/recipes/_generated';
import { usePath } from '@/router';

const RECIPE_ROUTE_RE = /^\/r\/([^/]+)\/?$/;
const BROWSE_ROUTE_RE = /^\/browse\/?$/;
const HOME_ROUTE_RE = /^\/$/;

interface PageMeta {
  title: string;
  ogType: 'website' | 'article';
  ogImage: string | null;
}

function metaFor(path: string): PageMeta {
  const recipeMatch = path.match(RECIPE_ROUTE_RE);
  if (recipeMatch?.[1]) {
    const entry = recipes.find((r) => r.slug === recipeMatch[1]);
    if (entry) {
      return {
        title: `yum | ${entry.recipe.title}`,
        ogType: 'article',
        ogImage: entry.hasOg ? `${window.location.origin}/img/recipes/${entry.slug}/og.jpg` : null,
      };
    }
  }
  return {
    title: BROWSE_ROUTE_RE.test(path) ? 'yum | Browse all' : 'yum',
    ogType: 'website',
    ogImage: null,
  };
}

function upsertMeta(attr: 'property' | 'name', key: string, content: string | null): void {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector(selector);
  if (content === null) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('content', content);
    return;
  }
  const el = document.createElement('meta');
  el.setAttribute(attr, key);
  el.setAttribute('content', content);
  document.head.appendChild(el);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function applyMeta(path: string): void {
  const m = metaFor(path);
  const canonical = `${window.location.origin}${path}`;
  document.title = m.title;
  upsertCanonical(canonical);
  upsertMeta('property', 'og:title', m.title);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:type', m.ogType);
  upsertMeta('property', 'og:image', m.ogImage);
  upsertMeta('property', 'og:image:type', m.ogImage ? 'image/jpeg' : null);
  upsertMeta('name', 'twitter:card', m.ogImage ? 'summary_large_image' : 'summary');
}

export function App() {
  const path = usePath();
  const recipeMatch = path.match(RECIPE_ROUTE_RE);
  const isBrowse = BROWSE_ROUTE_RE.test(path);

  useEffect(() => {
    applyMeta(path);
  }, [path]);

  return (
    <div class="app">
      <Header />
      <main class="app__main">
        {recipeMatch?.[1] ? (
          <RecipeView slug={recipeMatch[1]} />
        ) : isBrowse ? (
          <Browse />
        ) : HOME_ROUTE_RE.test(path) ? (
          <RecipeList />
        ) : (
          <NotFound />
        )}
      </main>
      <Footer />
    </div>
  );
}
